import React, { useEffect, useRef, useState } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
    Sparkles, ExternalLink, RefreshCw, Receipt,
    ChevronDown, ChevronUp, Search, X,
} from "lucide-react";

function fmtMoney(cents) {
    if (cents == null) return "—";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function MarkupSourceBadge({ source, pct }) {
    if (source === "customer_override") {
        return (
            <span className="inline-flex items-center rounded-full bg-violet-50 text-violet-800 ring-1 ring-violet-200 px-2 py-0.5 text-[10px] font-medium">
                Override {pct}%
            </span>
        );
    }
    return (
        <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200 px-2 py-0.5 text-[10px] font-medium">
            Default {pct}%
        </span>
    );
}

function BillsIncludedExpander({ bills }) {
    const [open, setOpen] = useState(false);
    if (!bills || bills.length === 0) return null;
    return (
        <div className="mt-1">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-1 text-[10.5px] text-slate-500 hover:text-slate-700"
                data-testid="toll-generate-bills-toggle"
            >
                {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                {open ? "Hide" : "See"} {bills.length} bill{bills.length === 1 ? "" : "s"} included
            </button>
            {open && (
                <ul className="mt-1 pl-4 space-y-0.5 text-[10.5px] font-mono text-slate-600">
                    {bills.map((bid) => (
                        <li key={bid} data-testid={`toll-generate-bill-included-${bid}`}>{bid}</li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/**
 * Phase TOLLBILL-P5b UX2 (2026-07-10) — Inline typeahead picker for the
 * "Generate draft toll invoices" modal. Replaces the prior UUID text
 * input with a debounced (300ms) name search against
 * ``GET /admin/customers?q=&limit=20``. Selected value surfaces as a
 * chip; the parent modal reads ``selected?.id`` for the generate call.
 * Blank picker == include all matched customers (unchanged default).
 *
 * Intentionally colocated with the modal — per the P5b UX2 spec this
 * picker is scoped to a single caller. Do not wire elsewhere; if a
 * second consumer arises, promote to ``/components/CustomerPicker``.
 */
function CustomerPicker({ value, onChange }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (value || !q.trim()) { setResults([]); return undefined; }
        setLoading(true);
        const handle = setTimeout(async () => {
            try {
                const { data } = await api.get("/admin/customers", {
                    params: { q: q.trim(), limit: 20 },
                });
                setResults(data.items || []);
                setOpen(true);
            } catch {
                setResults([]);
            } finally { setLoading(false); }
        }, 300);
        return () => clearTimeout(handle);
    }, [q, value]);

    // Close dropdown on outside click.
    useEffect(() => {
        const onDown = (e) => {
            if (!wrapRef.current) return;
            if (!wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, []);

    if (value) {
        return (
            <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2" data-testid="toll-generate-customer-chip">
                <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-900 truncate" title={value.company_name}>
                        {value.company_name || `${value.id.slice(0, 8)}…`}
                    </p>
                    <p className="text-[10.5px] font-mono text-slate-500 truncate" title={value.id}>{value.id}</p>
                </div>
                <button
                    type="button"
                    onClick={() => { onChange(null); setQ(""); setResults([]); }}
                    className="text-slate-500 hover:text-slate-800"
                    aria-label="Clear customer selection"
                    data-testid="toll-generate-customer-clear"
                >
                    <X size={14} />
                </button>
            </div>
        );
    }

    return (
        <div ref={wrapRef} className="relative">
            <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onFocus={() => q.trim() && setOpen(true)}
                    placeholder="Search by name, or leave blank for all matched customers"
                    className="pl-9"
                    data-testid="toll-generate-customer-picker-input"
                    autoComplete="off"
                />
            </div>
            {open && q.trim() && (
                <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-64 overflow-y-auto" data-testid="toll-generate-customer-picker-menu">
                    {loading && <p className="p-3 text-xs text-slate-500">Searching…</p>}
                    {!loading && results.length === 0 && (
                        <p className="p-3 text-xs text-slate-500" data-testid="toll-generate-customer-picker-empty">No customers match “{q.trim()}”.</p>
                    )}
                    {!loading && results.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            onClick={() => { onChange(c); setOpen(false); setQ(""); }}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                            data-testid={`toll-generate-customer-picker-option-${c.id}`}
                        >
                            <p className="text-sm text-slate-900 truncate">{c.company_name || "(no name)"}</p>
                            <p className="text-[10.5px] font-mono text-slate-500 truncate">{c.id}</p>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Phase TOLLBILL-P3a → P5c (2026-07-10) — Manual draft toll-invoice
 * generator modal. Rendered from both the Bills page (primary) and the
 * legacy Transactions page.
 *
 * Two-step UX (safety-by-default):
 *   Step 1 — Dry run: renders per-invoice plan (customer, bill count,
 *     base + markup + fees, total, markup source badge, bills_included).
 *   Step 2 — Confirm & persist: same call w/ dry_run=false; renders
 *     created invoices with deep-links + bills_included list.
 *
 * P5c updates:
 *   - shows bills_count instead of transactions_count
 *   - markup source badge (customer_override | global_default)
 *   - "See bills included" expander per row
 *   - per-invoice fee breakdown (processing / late / previous balance)
 */
export default function TollGenerateInvoicesModal({ open, onOpenChange, onCreated }) {
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [dryRunResult, setDryRunResult] = useState(null);
    const [persistResult, setPersistResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const reset = () => {
        setSelectedCustomer(null);
        setDryRunResult(null);
        setPersistResult(null);
        setBusy(false);
    };

    const close = () => {
        if (busy) return;
        reset();
        onOpenChange(false);
    };

    const runDry = async () => {
        setBusy(true);
        setPersistResult(null);
        try {
            const body = { dry_run: true };
            if (selectedCustomer?.id) body.customer_id = selectedCustomer.id;
            const { data } = await api.post("/admin/tolls/invoices/generate", body);
            setDryRunResult(data);
            const nCust = data.customers_considered || 0;
            const nBills = data.bills_selected ?? data.transactions_selected ?? 0;
            if (nCust === 0) {
                toast.info("Nothing to invoice — no matched, undisputed, unbilled bills.");
            } else {
                toast.success(`Dry run: ${nCust} customer(s), ${nBills} bill(s).`);
            }
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const runPersist = async () => {
        setBusy(true);
        try {
            const body = { dry_run: false };
            if (selectedCustomer?.id) body.customer_id = selectedCustomer.id;
            const { data } = await api.post("/admin/tolls/invoices/generate", body);
            setPersistResult(data);
            const n = data.invoices_created || 0;
            toast.success(`Created ${n} draft invoice${n === 1 ? "" : "s"}.`);
            if (onCreated) onCreated(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => v ? onOpenChange(true) : close()}>
            <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="toll-generate-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles size={16} className="text-blue-900" /> Generate draft toll invoices
                    </DialogTitle>
                    <DialogDescription>
                        Batches matched, undisputed, unbilled toll bills by customer into standalone draft invoices. Markup applies only to toll amounts — processing, late, and previous-balance fees pass through untouched. Always dry-runs first.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <Label className="text-xs">Customer (optional)</Label>
                        <CustomerPicker
                            value={selectedCustomer}
                            onChange={setSelectedCustomer}
                        />
                        <p className="text-[11px] text-slate-500 mt-1">
                            Search by company name to scope this run to a single customer, or leave blank to include every matched-but-unbilled customer.
                        </p>
                    </div>

                    {!persistResult && (
                        <div className="flex flex-wrap gap-2">
                            <Button
                                onClick={runDry}
                                disabled={busy}
                                variant="outline"
                                className="gap-1.5"
                                data-testid="toll-generate-dry-run"
                            >
                                <RefreshCw size={14} /> {dryRunResult ? "Re-run dry run" : "Dry run"}
                            </Button>
                            {dryRunResult && dryRunResult.customers_considered > 0 && (
                                <Button
                                    onClick={runPersist}
                                    disabled={busy}
                                    className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                                    data-testid="toll-generate-persist"
                                >
                                    <Sparkles size={14} /> Confirm &amp; persist
                                </Button>
                            )}
                        </div>
                    )}

                    {dryRunResult && !persistResult && (
                        <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="toll-generate-dry-plan">
                            <p className="text-xs font-medium text-slate-600 mb-2">
                                Dry-run plan — {dryRunResult.customers_considered} customer(s), {dryRunResult.bills_selected ?? 0} bill(s):
                            </p>
                            {dryRunResult.customers_considered === 0 ? (
                                <p className="text-sm text-slate-500">Nothing to invoice.</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-xs">
                                        <thead className="text-slate-500 uppercase">
                                            <tr>
                                                <th className="text-left py-1 pr-3">Customer</th>
                                                <th className="text-right py-1 px-2">Bills</th>
                                                <th className="text-right py-1 px-2">Tolls (base)</th>
                                                <th className="text-right py-1 px-2">Markup</th>
                                                <th className="text-right py-1 px-2">Fees</th>
                                                <th className="text-right py-1 pl-2">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {(dryRunResult.invoices_preview || []).map((row) => {
                                                const feeSum = (row.processing_fee_cents || 0) + (row.late_fees_cents || 0) + (row.previous_balance_cents || 0);
                                                return (
                                                    <tr key={row.customer_id} data-testid={`toll-generate-plan-row-${row.customer_id}`}>
                                                        <td className="py-1.5 pr-3">
                                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                                {/* Phase TOLLBILL-P5b UX (2026-07-10) — prefer the
                                                                    denormalized customer_name (joined server-side);
                                                                    fall back to a shortened UUID so the UI doesn't
                                                                    silently break if the join misses. */}
                                                                <span
                                                                    className="text-slate-800 truncate max-w-[220px]"
                                                                    title={row.customer_name || row.customer_id}
                                                                    data-testid={`toll-generate-plan-customer-${row.customer_id}`}
                                                                >
                                                                    {row.customer_name || `${row.customer_id.slice(0, 8)}…`}
                                                                </span>
                                                                <MarkupSourceBadge source={row.markup_source} pct={row.markup_percent} />
                                                            </div>
                                                            <BillsIncludedExpander bills={row.bills_included} />
                                                        </td>
                                                        <td className="py-1.5 px-2 text-right tabular-nums" data-testid={`toll-generate-plan-bills-count-${row.customer_id}`}>
                                                            {row.bills_count ?? (row.bills_included || []).length}
                                                        </td>
                                                        <td className="py-1.5 px-2 text-right tabular-nums">{fmtMoney(row.base_cents)}</td>
                                                        <td className="py-1.5 px-2 text-right tabular-nums text-blue-900">{fmtMoney(row.markup_cents)}</td>
                                                        <td className="py-1.5 px-2 text-right tabular-nums text-slate-600"
                                                            title={`Processing ${fmtMoney(row.processing_fee_cents || 0)} · Late ${fmtMoney(row.late_fees_cents || 0)} · Prev bal ${fmtMoney(row.previous_balance_cents || 0)}`}>
                                                            {feeSum > 0 ? fmtMoney(feeSum) : <span className="text-slate-300">—</span>}
                                                        </td>
                                                        <td className="py-1.5 pl-2 text-right font-semibold tabular-nums">{fmtMoney(row.total_cents)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {persistResult && (
                        <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3" data-testid="toll-generate-persist-result">
                            <p className="text-sm font-medium text-emerald-900 mb-2 flex items-center gap-1.5">
                                <Receipt size={14} /> Created {persistResult.invoices_created} draft invoice{persistResult.invoices_created === 1 ? "" : "s"} ·{" "}
                                {persistResult.bills_invoiced ?? 0} bill{(persistResult.bills_invoiced ?? 0) === 1 ? "" : "s"} invoiced ·{" "}
                                {persistResult.transactions_invoiced} txn{persistResult.transactions_invoiced === 1 ? "" : "s"} cascaded.
                            </p>
                            {(persistResult.invoices || []).length > 0 && (
                                <ul className="space-y-2">
                                    {persistResult.invoices.map((inv) => (
                                        <li key={inv.id} className="text-xs" data-testid={`toll-generate-invoice-${inv.id}`}>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Link to={`/admin/invoices/${inv.id}`} className="text-blue-900 hover:underline inline-flex items-center gap-1 font-mono font-semibold">
                                                    {inv.invoice_number} <ExternalLink size={11} />
                                                </Link>
                                                <span className="text-slate-800 truncate max-w-[200px]" title={inv.customer_name || inv.customer_id}>
                                                    {inv.customer_name || `${inv.customer_id.slice(0, 8)}…`}
                                                </span>
                                                <MarkupSourceBadge source={inv.markup_source} pct={inv.markup_percent} />
                                                <span className="text-slate-500">
                                                    {inv.bills_count ?? (inv.bills_included || []).length} bill · {fmtMoney(inv.total_cents)} total
                                                </span>
                                            </div>
                                            <BillsIncludedExpander bills={inv.bills_included} />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={close} disabled={busy} data-testid="toll-generate-close">
                        {persistResult ? "Done" : "Cancel"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
